import React from "react";
import BlogStyle from "../../../assets/stylesheets/blog/BlogDetail.module.css";
import SignupForm from "../../../layouts/SignupForm";
import FooterStyle from "../../../assets/stylesheets/Footer.module.css";
import AppStyle from "../../../assets/stylesheets/App.module.css";
import WPPostsPage from "../blog-categories/WPPostsPage";  
import { useNavigate } from "react-router-dom";
import slugify from "slugify";

// Local blog images
import FamilyMain from "../../../assets/img/blog/familyMain.webp";
import AD02 from "../../../assets/img/blog/ad02.webp";
import AD04 from "../../../assets/img/blog/ad04.webp";

// API: farms
import { getFarmById } from "../../../api/farm-service/farmService";
// cover image API
import { getCoverImage } from "../../../api/farm-service/imageService";

// Target farms by ID (only title needed for now)
const TARGET_FARM_IDS = [289, 290, 291, 110, 292, 3, 294, 296, 295, 297];
// Manual descriptions per farm ID (edit freely)
const FARM_DESCRIPTIONS = {
  289: 'Meet Santa and his cheerful elves, take festive family photos, and enjoy cozy countryside fun with plenty of seasonal activities',
  290: 'Ride the tractor through the fields, meet friendly farm animals, and warm up by the fire - a true holiday wonderland for kids',
  291: 'Hop on a train or horse-drawn wagon, listen to live holiday music, and visit Santa in a joyful Christmas village.',
  110: 'Spend a full day of adventure with zip lines, a petting zoo, wagon rides, and festive treats. Perfect for family holiday memories!',
  292: 'Cut your own Christmas tree, visit Santa and relax around the campfire with hot cocoa and sweet holiday snacks.',
  3:   'Meet Santa and Mrs. Claus, explore the Maple Syrup Museum, and find the perfect tree at this charming heritage farm.',
  294: 'Join the family fun with wagon rides, scavenger hunts, and free hot chocolate. It is a great place to start new holiday traditions.',
  296: 'Kids can write letters to Santa, visit the Farm Hopper Barnyard, and pick their own Christmas tree in a magical setting.',
  295: 'A lively family destination featuring holiday markets, farm animals, wagon rides, and photo ops with Santa.',
  297: 'Cut your own spruce or pine, let the kids play in the outdoor area, and enjoy complimentary tree wrapping and wagon rides.'
};

// Reusable Best-of component (renders the list once)
function BestOfList({ className = "" }) {
  return (
    <div className={`${BlogStyle["card-left"]} ${className}`}>
      <div className={BlogStyle["car-title"]}>All our Best of lists</div>
      <div className={BlogStyle["related-subtitle"]}>Christmas</div>
      <ul className={BlogStyle["related-list"]}>
        <li><a href="https://farmciti.com/farm/233/thunderbird-farm">Thunderbird Farm</a></li>
        <li><a href="https://farmciti.com/farm/4/kustermans-adventure-farm">Kustermans Adventure Farm</a></li>
        <li><a href="https://farmciti.com/farm/181/wayne-gretzky-estates">Wayne Gretzky Estates</a></li>
        <li><a href="https://farmciti.com/farm/129/peller-estates-winery-and-restaurant">Peller Estates Winery & Restaurant</a></li>
        <li><a href="https://farmciti.com/farm/180/trius-winery-and-restaurant">Trius Winery & Restaurant</a></li>
        <li><a href="https://farmciti.com/farm/126/the-hare-wine-co">The Hare Wine Co.</a></li>
        <li><a href="https://farmciti.com/farm/176/ravine-vineyard-estate-winery">Ravine Vineyard Estate Winery</a></li>
        <li><a href="https://farmciti.com/farm/144/closson-chase">Closson Chase</a></li>
        <li><a href="https://farmciti.com/farm/170/norman-hardie-winery-and-vineyard">Norman Hardie Winery</a></li>
        <li><a href="https://farmciti.com/farm/62/niemi-family-farm">Niemi Family Farm</a></li>
      </ul>
    </div>
  );
}

export default function FamilyBlogPage() {
  const [showTopAd, setShowTopAd] = React.useState(true);
  const navigate = useNavigate();
  const [heroLoaded, setHeroLoaded] = React.useState(false);

  // Items: we will fill title from DB; img/desc/url left empty
  const [items, setItems] = React.useState(
    TARGET_FARM_IDS.map(id => ({ id, title: "", img: null, desc: FARM_DESCRIPTIONS[id] || "", url: "" }))
  );
  const [loadingItems, setLoadingItems] = React.useState(true);
  const [imgLoaded, setImgLoaded] = React.useState({});
  const markLoaded = (idx) => setImgLoaded((p) => ({ ...p, [idx]: true }));

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoadingItems(true);
        setImgLoaded({});
        const results = await Promise.all(
          TARGET_FARM_IDS.map(async (id) => {
            try {
              const farmRes = await getFarmById(id);
              const f = farmRes?.data; // actual farm data
              const name = f?.name || '';

              // Extract initial cover
              let coverUrl = f?.coverImage?.viewUrl || f?.coverImage?.url || null;
              if (!coverUrl && Array.isArray(f?.images) && f.images.length > 0) {
                coverUrl = f.images[0]?.viewUrl || f.images[0]?.url || null;
              }

              // Try presigned endpoint only if missing
              if (!coverUrl) {
                try {
                  const coverRes = await getCoverImage(id);
                  const c = coverRes?.data || coverRes;
                  coverUrl = c?.viewUrl || c?.url || coverUrl;
                } catch (e) {
                }
              }

              return { id, title: name, img: coverUrl };
            } catch (e) {
              return { id, title: '', img: null };
            }
          })
        );
        const next = results.map(r => ({ id: r.id, title: r.title, img: r.img, desc: FARM_DESCRIPTIONS[r.id] || '', url: '' }));
        if (!cancelled) setItems(next);
      } catch (e) {
        if (!cancelled) setItems(TARGET_FARM_IDS.map(() => ({ title: '', img: null, desc: '', url: '' })));
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, []);

  const renderSkeletonCard = (idx) => (
    <div key={`skeleton-${idx}`} className={`${BlogStyle["card"]} ${BlogStyle["card-skeleton"]}`}>
      <div className={BlogStyle['card-img-container']}>
        <div className={BlogStyle["card-img-skeleton-static"]} />
      </div>
      <div className={BlogStyle["card-header"]}>
        <span className={BlogStyle["badge-number-skeleton"]} aria-hidden="true" />
        <div className={BlogStyle["card-title-skeleton"]} style={{ width: '60%' }} aria-hidden="true" />
      </div>
      <div className={BlogStyle["card-desc-skeleton-group"]} aria-hidden="true">
        <div className={BlogStyle["card-desc-skeleton"]} style={{ width: '90%' }} />
        <div className={BlogStyle["card-desc-skeleton"]} style={{ width: '95%' }} />
        <div className={BlogStyle["card-desc-skeleton"]} style={{ width: '70%' }} />
      </div>
    </div>
  );

  const renderFarmCard = (it, idx) => (
    <a
      key={`${it.title || "item"}-${idx}`}
      className={BlogStyle["card"]}
      href={it.url || "#"}
      onClick={(e) => {
        e.preventDefault();
        const slug = slugify(it.title || '', { lower: true, strict: true });
        navigate(`/farm/${it.id}/${slug}`);
      }}
    >
      <div className={BlogStyle['card-img-container']}>
        <img
          className={BlogStyle["card-img"]}
          src={it.img || "/assets-placeholder/no-image.png"}
          alt={it.title || `Item ${idx + 1}`}
          loading="lazy"
          decoding="async"
          onLoad={() => markLoaded(idx)}
          onError={(e) => { e.currentTarget.src = '/assets-placeholder/no-image.png'; markLoaded(idx); }}
        />
        {!imgLoaded[idx] && <div className={BlogStyle["card-img-skeleton"]} />}
      </div>
      <div className={BlogStyle["card-header"]}>
        <span className={BlogStyle["badge-number"]}>{idx + 1}</span>
        <h2 className={BlogStyle["card-title"]}>{it.title || ""}</h2>
      </div>
      {it.desc && <p className={BlogStyle["card-desc"]}>{it.desc}</p>}
    </a>
  );

  return (
    <article className={BlogStyle["page"]}>
      {/* Top banner advertisement under header */}
      {/* {showTopAd && (
        <section className={BlogStyle["top-ad"]} role="complementary" aria-label="Advertisement">
          <button
            type="button"
            className={BlogStyle["top-ad-close"]}
            aria-label="Close advertisement"
            onClick={() => setShowTopAd(false)}
          >
            ×
          </button>
          <img
            className={BlogStyle["top-ad-img"]}
            src="https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=1920&auto=format&fit=crop"
            alt="Sponsored banner"
          />
          <div className={BlogStyle["top-ad-overlay"]}>
            <a
              className={BlogStyle["top-ad-btn"]}
              href="https://www.example.com"
              target="_blank"
              rel="noopener sponsored"
            >
              Search homes
            </a>
          </div>
        </section>
      )} */}

      {/* Top meta like blogTO */}
      <div className={BlogStyle["top-meta"]}>
        {/* <span className={BlogStyle["category-name"]}>Best of Winter</span>
        <span className={BlogStyle["sep"]}>•</span>
        <span>FarmCiti Team</span>
        <span className={BlogStyle["sep"]}>•</span>
        <span>Posted today</span> */}
      </div>

      {/* Big title */}
      <h1 className={BlogStyle["title"]}>Family & Kids-Friendly Christmas Tree Farms in Ontario</h1>

      {/* Hero image */}
      <div className={BlogStyle["hero-wrap"]}>
        {!heroLoaded && <div className={BlogStyle["hero-skeleton"]} aria-hidden="true" />}
        <img
          className={BlogStyle["hero"]}
          src={FamilyMain}
          alt="Winter farm hero"
          onLoad={() => setHeroLoaded(true)}
          onError={() => setHeroLoaded(true)}
          style={{ opacity: heroLoaded ? 1 : 0, transition: 'opacity 160ms ease' }}
        />
      </div>

      {/* Main body under hero with gap, using left sidebar layout */}
      <section className={BlogStyle["content-wrap-left"]}>
        {/* Sidebar on the left */}
        <aside className={`${BlogStyle["sidebar"]} ${BlogStyle["sidebar-left"]}`}>
          {/* <div className={BlogStyle["ad-box"]} role="complementary" aria-label="Advertisement">
            <img className={BlogStyle["ad-media"]} src={AD02} alt="Advertisement" />
          </div> */}

          <div className={BlogStyle["newsletter"]}>
            <div className={FooterStyle["footer-column"]}>
              <SignupForm />
            </div>
          </div>

          <div className={BlogStyle["hr"]} />

          {/* Desktop/Tablet: show in sidebar */}
          <BestOfList className={BlogStyle["bestof-desktop"]} />
        </aside>

        {/* Main content on the right */}
        <main className={BlogStyle["content"]}>
          <p className={BlogStyle["lead"]}>
            Looking to make magical holiday memories this season? Discover the best family-friendly Christmas tree farms in Ontario, where kids can meet Santa, ride wagons and tractors, feed farm animals, and enjoy cozy campfires. These farms offer a full festival-style holiday experience that the whole family will love.
            <br/><br/>(Ontario Christmas tree farms with Santa visits, wagon rides, farm animals, and festive holiday activities for kids)
  
          </p>
          
          <div className={BlogStyle["hr"]} />
          {/* Items grouped as: 1-2, 3-4, 5(single), 6-7, 8-9, 10(single), ... */}
          {(() => {
            const groups = [];
            const total = loadingItems ? TARGET_FARM_IDS.length : items.length;
            for (let i = 0; i < total; ) {
              const idxInCycle = i % 5;
              const groupSize = idxInCycle === 4 ? 1 : 2; // every 5th starts single
              const chunkIndices = [];
              for (let j = 0; j < groupSize && i + j < total; j += 1) {
                chunkIndices.push(i + j);
              }
              groups.push(
                <React.Fragment key={`group-${i}`}>
                  {groupSize === 2 ? (
                    <div className={BlogStyle["grid2"]}>
                      {chunkIndices.map((idx) => (
                        loadingItems ? renderSkeletonCard(idx) : renderFarmCard(items[idx], idx)
                      ))}
                    </div>
                  ) : (
                    (() => {
                      const idx = chunkIndices[0];
                      return loadingItems ? renderSkeletonCard(idx) : renderFarmCard(items[idx], idx);
                    })()
                  )}
                  {i + groupSize < total && <div className={BlogStyle["hr"]} />}
                </React.Fragment>
              );
              i += groupSize;
            }
            return groups;
          })()}

        </main>

        {/* Mobile: show below items and above map */}
        <BestOfList className={BlogStyle["bestof-mobile"]} />
      </section>

        {/* Map section (Google Maps embed without API) */}
        <div className={BlogStyle["hr"]} />
        <section className={BlogStyle["map-wrap"]} aria-label="Map of Ontario winter farms">
          {/* Crop the My Maps header by shifting the iframe up inside a clipped container */}
          <div className={BlogStyle["map-clip"]}>
            <iframe 
              className={BlogStyle["map-frame"]}
              title="Google Map — Ontario winter farms"
              src="https://www.google.com/maps/d/embed?mid=1mr1CNgTFovHvcTwaveRBQbEJUvFsjRA&ehbc=2E312F" 
              loading="lazy"
            />
          </div>
          
        </section>


      {/* Bottom share/ad area */}
      {/* <div className={BlogStyle["hr"]} />
      <div className={BlogStyle["ad-box"]} role="complementary" aria-label="Advertisement">
        <img className={`${BlogStyle["ad-media"]} ${BlogStyle["ad-media-fixed"]}`} src={AD04} alt="Advertisement" />
      </div> */}

      {/* blog section */}
      <section className={`${AppStyle["section"]} ${AppStyle["card-section"]}`}>
        <WPPostsPage />
      </section>
    </article>
  );
}
